import { useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const GoogleAuthSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false); // Prevent double execution

  // Extract params once on mount to avoid stale closure issues
  const params = useMemo(() => ({
    accessToken: searchParams.get('access_token'),
    refreshToken: searchParams.get('refresh_token'),
    profileComplete: searchParams.get('profile_complete') === 'true'
  }), [searchParams]);

  useEffect(() => {
    // Prevent multiple executions (React Strict Mode, or accidental re-renders)
    if (hasProcessed.current) {
      console.log('⏭️ GoogleAuthSuccess: Already processed, skipping');
      return;
    }

    const handleGoogleCallback = async () => {
      const { accessToken, refreshToken, profileComplete } = params;

      // Mark as processed immediately to prevent race conditions
      hasProcessed.current = true;

      console.log('📥 Google OAuth callback received');
      console.log('  - Access Token:', accessToken ? '✅ Present' : '❌ Missing');
      console.log('  - Refresh Token:', refreshToken ? '✅ Present' : '❌ Missing');
      console.log('  - Profile Complete:', profileComplete);

      if (!accessToken || !refreshToken) {
        console.error('❌ Missing tokens in callback');
        // Reset guard to allow retry if user comes back
        hasProcessed.current = false;
        navigate('/login');
        return;
      }

      try {
        // Store tokens with correct keys (matching apiService and authService)
        const tokenKey = import.meta.env.VITE_ACCESS_TOKEN_KEY || 'token';
        const refreshTokenKey = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refreshToken';
        const userKey = import.meta.env.VITE_USER_DATA_KEY || 'user';

        localStorage.setItem(tokenKey, accessToken);
        localStorage.setItem(refreshTokenKey, refreshToken);
        console.log('💾 Tokens stored in localStorage with correct keys:', { tokenKey, refreshTokenKey });

        // Update apiService with the new token
        apiService.setToken(accessToken);
        console.log('🔑 API service token updated');

        // Fetch user profile to update auth context
        const profileResponse = await apiService.getProfile();
        if (profileResponse && profileResponse.user) {
          localStorage.setItem(userKey, JSON.stringify(profileResponse.user));
          console.log('👤 User profile fetched and stored');
        }

        // Refresh auth context
        await refreshUser();

        // Redirect based on profile completion status
        if (profileComplete) {
          console.log('✅ Profile complete, redirecting to profile');
          setTimeout(() => navigate('/profile'), 500);
        } else {
          console.log('⚠️ Profile incomplete, redirecting to complete-profile');
          setTimeout(() => navigate('/auth/complete-profile'), 500);
        }
      } catch (error) {
        console.error('❌ Error processing Google callback:', error);
        // Reset guard to allow retry if user comes back
        hasProcessed.current = false;
        navigate('/login');
      }
    };

    handleGoogleCallback();
  }, [params, navigate, refreshUser]); // Proper dependencies

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default GoogleAuthSuccess;
